// api
import type {
  EditAlarmGroupDto,
  EditAlarmSubscriptionDto,
  ReadAlarmGroupDto,
  WriteAlarmInclusionDto,
} from '@/api/generated/recalc/endpoints/v1/alarms';

// relative
import type { AlarmGroupUserOption } from './createAlarmGroupForm.mappers';
import { DEFAULT_ALARM_GROUP_SUBSCRIPTION } from './createAlarmGroupForm.mappers';
import type { EditAlarmGroupFormValues } from './editAlarmGroupForm.schema';

export function toEditAlarmGroupFormValues(
  alarmGroup: ReadAlarmGroupDto,
  userOptions: AlarmGroupUserOption[],
): EditAlarmGroupFormValues {
  const mappedUsers = alarmGroup.AlarmSubscriptions?.map((subscription) => {
    const matchedOption = userOptions.find(
      (option) => option.User?.UserId === subscription.User?.UserId,
    );

    return {
      user: matchedOption?.User?.Email ?? '',
      feedSeverity: subscription.FeedSeverity ?? 'Information',
      emailSeverity: subscription.EmailSeverity ?? 'Information',
    };
  });

  const includedAlarmTypeIds =
    alarmGroup.AlarmInclusions?.map(
      (inclusion) => inclusion.AlarmType.AlarmTypeId,
    ) ?? [];

  return {
    groupName: alarmGroup.Name ?? '',
    users: mappedUsers?.length
      ? mappedUsers
      : [DEFAULT_ALARM_GROUP_SUBSCRIPTION],
    alarmTypes: includedAlarmTypeIds,
  };
}

export function toEditAlarmGroupDto(
  values: EditAlarmGroupFormValues,
  userOptions: AlarmGroupUserOption[],
): EditAlarmGroupDto {
  const alarmSubscriptions: EditAlarmSubscriptionDto[] = values.users
    .filter((subscription) => Boolean(subscription.user))
    .map((subscription): EditAlarmSubscriptionDto | null => {
      const matchedUser = userOptions.find(
        (option) => option.User?.Email === subscription.user,
      );

      if (!matchedUser?.User?.UserId) {
        return null;
      }

      return {
        User: { UserId: matchedUser.User.UserId },
        FeedSeverity: subscription.feedSeverity,
        EmailSeverity: subscription.emailSeverity,
      };
    })
    .filter((subscription): subscription is EditAlarmSubscriptionDto =>
      Boolean(subscription),
    );

  const alarmInclusions: WriteAlarmInclusionDto[] = values.alarmTypes.map(
    (alarmTypeId) => ({
      AlarmType: { AlarmTypeId: alarmTypeId },
    }),
  );

  return {
    Name: values.groupName.trim(),
    AlarmSubscriptions: alarmSubscriptions,
    AlarmInclusions: alarmInclusions,
  };
}
